import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { App, Alert, Button, Flex, Input, Popover, Select, Space, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, PlusOutlined, ThunderboltOutlined, WarningOutlined } from "@ant-design/icons";
import { useSWRConfig } from "swr";
import CurrencyAmount from "src/components/CurrencyAmount";
import CurrencyAmountInput from "src/components/CurrencyAmountInput";
import { WalletBalanceDisplay } from "src/components/wallet/WalletBalanceDisplay";
import { EarlyPaymentInfoModal } from "src/components/wallet/EarlyPaymentInfo";
import EarlyPaymentBreakdown from "src/components/wallet/EarlyPaymentBreakdown";
import FirstEarlyPaymentBadge from "src/components/wallet/FirstEarlyPaymentBadge";
import OTPModal from "src/components/OTPModal";
import useUser from "src/hooks/useUser";
import useIsDesktop from "src/hooks/useIsDesktop";
import useOrg from "src/hooks/apis/useOrg";
import useEarlyPaymentStatus from "src/hooks/apis/useEarlyPaymentStatus";
import useHttpClient from "src/hooks/useHttpClient";
import { toLatinDigit } from "src/utils/digits";
import {
  calcFeeAmount,
  calcTotalFee,
  amountAfterFeeEqualsBalance,
} from "src/utils/transactionFees";
import { trackWithdrawRequest } from "src/utils/gtm";
const MIN_WITHDRAW_USDT = 100;
const USDT_DECIMALS = 2;
const USDT_NETWORKS = [
  { value: "BEP20", label: <div className="flex items-center gap-1"><img src="/bnb-logo.svg" className="w-4 h-4" /> BEP20 (بایننس اسمارت چین) </div> },
  { value: "TRC20", label: <div className="flex items-center gap-1"><img src="/tron-logo.png" className="w-4 h-4" /> TRC20 (ترون) </div> },
] as const;
type UsdtNetwork = (typeof USDT_NETWORKS)[number]["value"];

/** BEP20: 0x + 40 hex. TRC20: T + 33 base58 chars. */
export function validateWalletAddress(
  address: string,
  network: "BEP20" | "TRC20"
): string | null {
  const trimmed = address.trim();
  if (!trimmed) return "لطفا آدرس کیف پول را وارد کنید";
  if (network === "BEP20") {
    if (!/^0x[0-9a-fA-F]{40}$/.test(trimmed))
      return "آدرس BEP20 معتبر نیست ";
    return null;
  }
  if (network === "TRC20") {
    if (!/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(trimmed))
      return "آدرس TRC20 معتبر نیست";
    return null;
  }
  return "شبکه نامعتبر است";
}

function parseNumber(value: string): number {
  const raw = toLatinDigit(value.replace(/[٬,]/g, ""));
  return parseFloat(raw) || 0;
}

function UsdtWithdrawForm() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const httpClient = useHttpClient();
  const { data: user } = useUser();
  const { data: org } = useOrg();
  const { data: epStatus } = useEarlyPaymentStatus();
  const isDesktop = useIsDesktop();

  const [usdtAmount, setUsdtAmount] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState<UsdtNetwork | null>(null);
  const [walletError, setWalletError] = useState("");
  const [amountError, setAmountError] = useState("");
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [epInfoOpen, setEpInfoOpen] = useState(false);
  const [pendingWithdraw, setPendingWithdraw] = useState<{
    amount: number;
    wallet: string;
    network: UsdtNetwork;
    isEp: boolean;
  } | null>(null);

  let {
    usdtFee,
    withdrawalsEnabled,
    usdtWithdrawalsEnabled,
    gbpToIrt = 0,
    usdtToIrt = 0,
  } = org?.config ?? {};
  const gbpCredit = user?.gbpCredit ?? 0;
  const balanceIrt = gbpCredit * gbpToIrt;
  const balanceUsdt =
    usdtToIrt > 0 && Number.isFinite(balanceIrt) ? balanceIrt / usdtToIrt : 0;
  const safeBalanceUsdt = Number.isFinite(balanceUsdt) ? balanceUsdt : 0;

  // USDT per 1 GBP (via the IRT cross-rate the rest of the form already uses).
  const gbpToUsdt = usdtToIrt > 0 ? gbpToIrt / usdtToIrt : 0;
  const epAvailable = epStatus?.available && (epStatus?.estimatedAmount ?? 0) > 0 && gbpToUsdt > 0;
  const epEstimatedGbp = epStatus?.estimatedAmount ?? 0;
  // Use the effective fee (discounted for a first early payment) so the breakdown
  // matches what the server will actually charge.
  const epFeePercent = epStatus?.effectiveFeePercent ?? epStatus?.feePercent ?? 0;
  const maxGbp = epAvailable ? gbpCredit + epEstimatedGbp : gbpCredit;
  const maxUsdtWithEp = epAvailable
    ? Math.floor(maxGbp * gbpToUsdt * 100) / 100
    : 0;
  const epUsdt = epEstimatedGbp * gbpToUsdt;

  const hasInsufficientCredit = gbpCredit < 30 && !epAvailable;
  const formDisabled = !withdrawalsEnabled || !usdtWithdrawalsEnabled;

  const amountUsdt = usdtAmount ? parseNumber(usdtAmount) : 0;
  // Early payment portion (the part of the withdrawal not covered by balance).
  const amountGbp = gbpToUsdt > 0 ? amountUsdt / gbpToUsdt : 0;
  const loanPortionGbp = Math.max(0, amountGbp - gbpCredit);
  const needsEarlyPayment = !!epAvailable && loanPortionGbp > 0.01;
  const epFeeGbp = needsEarlyPayment ? Math.ceil(loanPortionGbp * epFeePercent) / 100 : 0;
  const epFeeUsdt = epFeeGbp * gbpToUsdt;
  const loanPortionUsdt = loanPortionGbp * gbpToUsdt;
  const fromBalanceUsdt = Math.max(0, amountUsdt - loanPortionUsdt);
  const transactionFee = calcFeeAmount(amountUsdt, usdtFee?.transaction);
  const withdrawFee = calcFeeAmount(amountUsdt, usdtFee?.withdraw);
  const totalFee =
    usdtFee && amountUsdt > 0
      ? calcTotalFee({ amount: amountUsdt, fee: usdtFee, decimals: USDT_DECIMALS })
      : 0;

  const maxWithdrawAmount =
    usdtFee && safeBalanceUsdt > 0
      ? amountAfterFeeEqualsBalance({
        balance: safeBalanceUsdt,
        fee: usdtFee,
        decimals: USDT_DECIMALS,
      })
      : Math.floor(safeBalanceUsdt * 100) / 100;

  const effectiveMaxUsdt = epAvailable ? maxUsdtWithEp : maxWithdrawAmount;

  // console.log({
  //   usdtToIrt,
  //   gbpToIrtRatio,
  //   balanceIrt,
  //   balanceIrtUSDT: balanceUsdt * usdtToIrt,
  //   balanceUsdt,
  //   safeBalanceUsdt,
  //   maxWithdrawAmount,
  //   totalFee,
  //   withdrawFee,
  //   transactionFee,
  // })
  const handleSubmitClick = () => {
    const numValue = parseNumber(usdtAmount);
    const wallet = walletAddress.trim();

    setWalletError("");
    setAmountError("");

    if (!wallet) {
      setWalletError("لطفا آدرس کیف پول مقصد را وارد کنید");
      return;
    }
    if (!selectedNetwork) {
      setWalletError("لطفا شبکه انتقال را انتخاب کنید");
      return;
    }
    const addrError = validateWalletAddress(wallet, selectedNetwork);
    if (addrError) {
      setWalletError(addrError);
      return;
    }
    if (!usdtAmount || !Number.isFinite(numValue) || numValue <= 0) {
      setAmountError("لطفا مبلغ برداشت را وارد کنید");
      return;
    }
    if (numValue < MIN_WITHDRAW_USDT) {
      setAmountError(
        `حداقل مبلغ برداشت ${MIN_WITHDRAW_USDT.toLocaleString("fa-IR")} USDT می‌باشد`
      );
      return;
    }
    if (epAvailable) {
      if (numValue > effectiveMaxUsdt) {
        setAmountError("مبلغ بیشتر از موجودی قابل برداشت است");
        return;
      }
    } else if (totalFee + numValue > safeBalanceUsdt) {
      setAmountError("موجودی شما کافی نیست");
      return;
    }
    setPendingWithdraw({ amount: numValue, wallet, network: selectedNetwork, isEp: needsEarlyPayment });
    setOtpModalOpen(true);
  };

  const handleOtpSuccess = () => {
    message.success(
      pendingWithdraw?.isEp
        ? "درخواست برداشت و پرداخت زودهنگام با موفقیت ثبت شد — در انتظار تأیید ادمین"
        : "درخواست برداشت با موفقیت ثبت شد"
    );
    mutate((key: unknown) => typeof key === "string" && key.startsWith("transactions-"));
    mutate("user");
    mutate("early-payment-status");
    mutate("loans");
    setOtpModalOpen(false);
    setPendingWithdraw(null);
    setUsdtAmount("");
    setWalletAddress("");
    setSelectedNetwork(null);
    navigate("/finance");
  };

  const handleOtpVerify = async (code: string) => {
    if (!pendingWithdraw) return;
    // Early payment goes through the integrated loan endpoint (still OTP-gated for
    // USDT via checkWithdrawOtp); plain withdrawals use the standard endpoint.
    const url = pendingWithdraw.isEp ? "/loans/withdraw" : "/transactions/withdraw";
    const response = await httpClient.post(
      url,
      {
        amount: pendingWithdraw.amount,
        currency: "USDT",
        wallet: pendingWithdraw.wallet,
        network: pendingWithdraw.network,
      },
      { headers: { OTP: code } }
    );
    const transaction = pendingWithdraw.isEp
      ? response.data.data?.withdrawal
      : response.data.data;
    trackWithdrawRequest({
      transaction_id: transaction?.id,
      currency: "USDT",
      withdraw_amount: pendingWithdraw.amount,
      available_balance: safeBalanceUsdt,
      early_income_credit: epAvailable ? epUsdt : 0,
      used_early_income: pendingWithdraw.isEp,
      early_income_used_amount: Math.max(0, pendingWithdraw.amount - safeBalanceUsdt),
    });
  };

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {formDisabled && (
        <Alert
          banner
          title="برداشت USDT در حال حاضر غیرفعال است."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
        />
      )}
      <div className="relative w-full">
        <div
          className={
            formDisabled
              ? "flex flex-col gap-4 select-none blur-[3px] opacity-60 transition-[filter,opacity]"
              : "flex flex-col gap-4"
          }
          aria-hidden={formDisabled}
        >
          <WalletBalanceDisplay
            primaryAmount={
              epAvailable
                ? maxUsdtWithEp.toLocaleString("en-US", { maximumFractionDigits: 2 })
                : Number.isFinite(balanceUsdt)
                  ? balanceUsdt.toLocaleString("en-US", { maximumFractionDigits: 2 })
                  : 0
            }
            primaryUnit="USDT"
            rateLabel="نرخ فعلی تتر: "
            rateAmount={usdtToIrt.toLocaleString("en-US", { maximumFractionDigits: 2 })}
          >
            {epAvailable && (
              <div className="mt-3">
                {/* Credit + early payment together on one small, muted row */}
                <Flex gap={32} wrap="wrap">
                  <div>
                    <Typography.Text type="secondary" className="block text-xs">دارایی</Typography.Text>
                    <CurrencyAmount amount={safeBalanceUsdt} currency="USDT" fontSize={13} />
                  </div>
                  <div>
                    <Typography.Text type="secondary" className="block text-xs">
                      <ThunderboltOutlined className="ml-1 text-amber-500" />
                      پرداخت زودهنگام
                      <Tooltip title="درباره‌ی پرداخت زودهنگام">
                        <InfoCircleOutlined
                          className="mr-1 cursor-pointer opacity-60 hover:opacity-100"
                          onClick={() => setEpInfoOpen(true)}
                        />
                      </Tooltip>
                    </Typography.Text>
                    <CurrencyAmount amount={epUsdt} currency="USDT" fontSize={13} />
                    <FirstEarlyPaymentBadge className="mt-1 mx-1" />
                  </div>
                </Flex>
                {/* Total below, label above value */}
                <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-2">
                  <Typography.Text type="secondary" className="block text-xs">مجموع (معادل پوند)</Typography.Text>
                  <CurrencyAmount amount={maxGbp} currency="GBP" fontSize={16} />
                </div>
              </div>
            )}
          </WalletBalanceDisplay>

          <div>
            <Typography.Text type="secondary" className="block mb-1">
              انتخاب شبکه
            </Typography.Text>
            <Select<UsdtNetwork>
              placeholder="شبکه انتقال"
              style={{ width: "100%" }}
              size="large"
              value={selectedNetwork}
              onChange={(v) => {
                setSelectedNetwork(v);
                setWalletError("");
              }}
              options={USDT_NETWORKS.map(({ value, label }) => ({ value, label, }))}
            />
          </div>

          <div>
            <Typography.Text type="secondary" className="block mb-1">
              آدرس کیف پول مقصد
            </Typography.Text>
            <Input
              placeholder="آدرس کیف پول مقصد را وارد کنید"
              size="large"
              value={walletAddress}
              onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                setWalletAddress(value);
                setWalletError("");
              }}
              status={walletError ? "error" : undefined}
            />
            {walletError && (
              <Typography.Text type="danger" className="text-xs mt-1 block">
                {walletError}
              </Typography.Text>
            )}
          </div>

          <Popover
            trigger={["hover", "click"]}
            content={
              <div className="max-w-xs text-right">
                دقت کنید شبکه انتخابی با شبکه‌ای که رمزارزهای شما بر روی آن است یکی باشد. واریز به شبکه اشتباه یا پشتیبانی نشده موجب از دست رفتن دارایی شما می‌شود.
              </div>
            }
          >
            <Typography.Text type="warning" className="text-sm cursor-help inline-flex items-center gap-1">
              <WarningOutlined />
              شبکه انتخابی باید با شبکه کیف پول مقصد یکی باشد
              <InfoCircleOutlined className="opacity-60" />
            </Typography.Text>
          </Popover>
          <div>
            <Flex justify="space-between" className="mb-1">
              <Typography.Text type="secondary">
                مقدار برداشت (USDT)
              </Typography.Text>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                className="p-0"
                onClick={() => setUsdtAmount(String(effectiveMaxUsdt))}
              >
                حد اکثر مبلغ
              </Button>
            </Flex>
            <CurrencyAmountInput
              placeholder={`حداقل ${MIN_WITHDRAW_USDT}`}
              currencyLabel="USDT"
              value={
                usdtAmount && Number.isFinite(parseNumber(usdtAmount))
                  ? parseNumber(usdtAmount).toLocaleString("en-US", {
                    maximumFractionDigits: 3,
                    minimumFractionDigits: 0,
                  })
                  : usdtAmount
              }
              status={amountError ? "error" : undefined}
              onChange={(value) => {
                const toLatin = toLatinDigit(value.replace(/[٬,]/g, ""));
                const valid =
                  toLatin === "" ||
                  (/^\d*\.?\d*$/.test(toLatin) &&
                    (Number.isNaN(parseFloat(toLatin)) || parseFloat(toLatin) <= 10_000_000));
                if (valid) {
                  setUsdtAmount(toLatin);
                  setAmountError("");
                }
              }}
            />
            {amountError && (
              <Typography.Text type="danger" className="text-xs mt-1 block">
                {amountError}
              </Typography.Text>
            )}
          </div>

          {/* Early payment breakdown */}
          {needsEarlyPayment && amountUsdt > 0 && (
            <EarlyPaymentBreakdown
              fromBalance={fromBalanceUsdt}
              loanPortion={loanPortionUsdt}
              epFee={epFeeUsdt}
              feePercent={epFeePercent}
              currency="USDT"
            />
          )}

          {!needsEarlyPayment && (
            <>
              {withdrawFee > 0 ? (
                <Flex justify="space-between" className="text-sm">
                  <Typography.Text type="secondary">کارمزد برداشت تتر</Typography.Text>
                  <CurrencyAmount
                    amount={Number.isFinite(withdrawFee) ? withdrawFee : 0}
                    currency="USDT"
                  />
                </Flex>
              ) : null}
              <Flex justify="space-between" className="text-sm">
                <Typography.Text type="secondary">هزینه انتقال</Typography.Text>
                {transactionFee === 0 || !Number.isFinite(transactionFee) ? (
                  <Typography.Text type="secondary">رایگان</Typography.Text>
                ) : (
                  <CurrencyAmount
                    amount={Number.isFinite(transactionFee) ? transactionFee : 0}
                    currency="USDT"
                  />
                )}
              </Flex>
            </>
          )}

          <Button
            type="primary"
            size="large"
            className="self-start"
            block={!isDesktop}
            disabled={hasInsufficientCredit || !usdtToIrt || formDisabled}
            onClick={handleSubmitClick}
          >
            درخواست برداشت
          </Button>
        </div>
        {formDisabled && (
          <div
            className="absolute inset-0 z-10 cursor-not-allowed"
            aria-hidden
          />
        )}
      </div>

      <OTPModal
        type="WITHDRAWAL_REQUEST"
        requestOtp
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setPendingWithdraw(null);
        }}
        onSuccess={handleOtpSuccess}
        onResend={() => { }}
        validationRequest={handleOtpVerify}
      />
      <EarlyPaymentInfoModal
        open={epInfoOpen}
        onClose={() => setEpInfoOpen(false)}
        feePercent={epStatus?.feePercent}
        percent={epStatus?.percent}
        firstFeePercent={epStatus?.firstEarlyPaymentFeePercent}
        firstEligible={epStatus?.firstEarlyPaymentEligible}
      />
    </Space>
  );
}

export default UsdtWithdrawForm;
